// Paste, drop, or pick an OpenSSH private key.
//
// Two forms collect one now — the add-worker wizard and the Settings key library
// — and the affordances are not obvious enough to reimplement twice. An operator
// with a key on disk should not have to `cat` it into a clipboard, and one who
// drags the file onto the box should not find that the box only takes text.
//
// The textarea is the drop target itself rather than a wrapper around one: it
// already takes this content by paste and is already focusable, so there is no
// second element needing a role.

import { Button } from "@veil/ui/components/button";
import {
	Field,
	FieldDescription,
	FieldError,
	FieldLabel,
} from "@veil/ui/components/field";
import { Textarea } from "@veil/ui/components/textarea";
import Upload from "lucide-react/dist/esm/icons/upload";
import { type ReactNode, useRef, useState } from "react";
import type { FieldError as RhfFieldError } from "react-hook-form";

export function PrivateKeyField({
	id,
	label = "Private key",
	description,
	error,
	rows = 5,
	registration,
	/** Called with the file's text once a drop or a file pick resolves. */
	onFileText,
}: {
	id: string;
	label?: ReactNode;
	description?: ReactNode;
	error?: RhfFieldError;
	rows?: number;
	/** The spread result of `form.register("…")`. */
	registration: React.ComponentProps<typeof Textarea>;
	onFileText: (text: string) => void;
}) {
	const fileInput = useRef<HTMLInputElement>(null);
	const [dropping, setDropping] = useState(false);

	const readKeyFile = async (file: File | undefined) => {
		if (!file) return;
		onFileText(await file.text());
	};

	return (
		<Field data-invalid={!!error}>
			<FieldLabel htmlFor={id}>{label}</FieldLabel>
			<Textarea
				id={id}
				rows={rows}
				spellCheck={false}
				autoComplete="off"
				aria-invalid={!!error}
				placeholder={"-----BEGIN OPENSSH PRIVATE KEY-----\n…"}
				className={`font-mono text-[11px] ${dropping ? "ring-2 ring-primary" : ""}`}
				onDragOver={(event) => {
					event.preventDefault();
					setDropping(true);
				}}
				onDragLeave={() => setDropping(false)}
				onDrop={(event) => {
					event.preventDefault();
					setDropping(false);
					void readKeyFile(event.dataTransfer.files[0]);
				}}
				{...registration}
			/>
			<FieldDescription>
				{description ?? "Paste it, or drop the key file here."}{" "}
				<Button
					type="button"
					variant="link"
					size="sm"
					className="h-auto p-0 align-baseline"
					onClick={() => fileInput.current?.click()}
				>
					<Upload className="size-3" /> Choose a file
				</Button>
				<input
					ref={fileInput}
					type="file"
					className="sr-only"
					tabIndex={-1}
					onChange={(event) => void readKeyFile(event.target.files?.[0])}
				/>
			</FieldDescription>
			<FieldError errors={[error]} />
		</Field>
	);
}
